import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Download, 
  FileSpreadsheet, 
  AlertCircle, 
  CheckCircle2 
} from "lucide-react";

export function BulkUploadModal({ 
  open, 
  onOpenChange, 
  uploadFile, 
  setUploadFile, 
  uploadValidation, 
  setUploadValidation, 
  uploading, 
  onDownloadTemplate, 
  onValidate, 
  onUpload 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Bulk Student Upload
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to enroll multiple students at once
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Download Template */}
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-600 mb-2">
              First, download our CSV template with the required columns:
            </p>
            <Button variant="outline" size="sm" onClick={onDownloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>

          {/* File Upload */}
          <div>
            <Label>Upload CSV File</Label>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => {
                setUploadFile(e.target.files?.[0] || null);
                setUploadValidation(null);
              }}
              className="mt-1"
              data-testid="csv-file-input"
            />
          </div>

          {/* Validation Results */}
          {uploadValidation && (
            <div className={`rounded-lg p-4 ${uploadValidation.valid ? "bg-green-50" : "bg-red-50"}`}>
              {uploadValidation.valid ? (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-700">Validation Passed</p>
                    <p className="text-sm text-green-600">
                      {uploadValidation.valid_rows} students ready to import
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700">Validation Failed</p>
                    <ul className="text-sm text-red-600 mt-1 list-disc list-inside">
                      {uploadValidation.errors?.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {(uploadValidation.errors?.length || 0) > 5 && (
                        <li>...and {uploadValidation.errors.length - 5} more errors</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => {
            onOpenChange(false);
            setUploadFile(null);
            setUploadValidation(null);
          }}>
            Cancel
          </Button>
          {!uploadValidation ? (
            <Button 
              onClick={onValidate}
              disabled={!uploadFile || uploading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="validate-csv-btn"
            >
              {uploading ? "Validating..." : "Validate File"}
            </Button>
          ) : uploadValidation.valid ? (
            <Button 
              onClick={onUpload}
              disabled={uploading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="upload-csv-btn"
            >
              {uploading ? "Uploading..." : `Import ${uploadValidation.valid_rows} Students`}
            </Button>
          ) : (
            <Button 
              onClick={() => {
                setUploadFile(null);
                setUploadValidation(null);
              }}
              variant="outline"
            >
              Try Again
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkUploadModal;
